import { Maybe } from '@gql/schema';
import {
  ElementName,
  ElementType,
  InteractionType,
  getTotalAdultsAndChildrenFromParam,
  trackContentInteraction,
} from '@secretescapes/cauldron';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import { IFlightState } from '@features/Flights';

interface NavigateToFlightsListArgs {
  startDate: string;
  endDate: string;
  totalPrice: number;
  offerId: string;
  availableRooms: number;
  saleId: string;
  flightState: IFlightState;
  affiliate: string;
  travellersSelectionParam?: string;
}

const navigateToFlightsList = ({
  startDate,
  endDate,
  totalPrice,
  offerId,
  availableRooms,
  saleId,
  flightState,
  affiliate,
  travellersSelectionParam,
}: NavigateToFlightsListArgs) => {
  const { selectedAirport } = flightState ?? {};
  const { adults: adultTravellers, children: childTravellers } = travellersSelectionParam
    ? getTotalAdultsAndChildrenFromParam(travellersSelectionParam)
    : {};

  // Book cta tracking
  trackContentInteraction(
    {
      elementName: 'select_flights' as ElementName,
      elementType: ElementType.BUTTON_TYPE_CTA,
    },
    InteractionType.CLICK,
    {
      saleId,
      // @ts-expect-error TODO: Add type to Cauldron ItemDetailsType
      numberOfAdults: adultTravellers,
      numberOfChildren: childTravellers,
    }
  );

  const url = new URL(window.location.toString());
  const pathElements = url.pathname.split('/').filter(element => element.length > 0);
  const saleSlug = pathElements[pathElements.length - 2] ?? pathElements[0];
  const flightsPath = `/flights/${saleSlug}/${saleId}`;

  const params = new URLSearchParams({
    offerId,
    startDate,
    endDate,
    departureAirport: selectedAirport.code,
    guests: travellersSelectionParam!,
    maxAvailableRooms: availableRooms.toString(),
    affiliate,
    price: totalPrice.toString(),
    sessionId: uuidv4(),
  });

  window.location.assign(`${flightsPath}?${params.toString()}`);
};

export interface NavigatePostSaleArgs {
  withFlights: boolean;
  startDate: string;
  endDate: string;
  totalPrice: number;
  duration: number;
  numberOfRooms: number;
  offerId: string;
  saleId: string;
  rateCode: string;
  availableRooms: number;
  flightState: IFlightState;
  affiliate: string;
  maxAdultsPerRoom?: Maybe<number>;
  travellersSelectionParam?: string;
}

export const navigatePostSale = ({
  withFlights,
  startDate,
  endDate,
  totalPrice,
  duration,
  numberOfRooms,
  flightState,
  offerId,
  saleId,
  rateCode,
  availableRooms,
  affiliate,
  maxAdultsPerRoom,
  travellersSelectionParam,
}: NavigatePostSaleArgs) => {
  if (withFlights) {
    navigateToFlightsList({
      startDate,
      endDate,
      totalPrice,
      availableRooms,
      flightState,
      offerId,
      saleId,
      affiliate,
      travellersSelectionParam,
    });
  } else {
    // Book cta tracking
    trackContentInteraction(
      {
        elementName: ElementName.BUTTON_NAME_BOOK,
        elementType: ElementType.BUTTON_TYPE_CTA,
      },
      InteractionType.CLICK,
      // @ts-expect-error TODO: Add type to Cauldron ItemDetailsType
      { saleId }
    );

    // New checkout page or old booking form
    if (travellersSelectionParam) {
      const nights = moment(endDate).startOf('d').diff(moment(startDate).startOf('d'), 'd');

      const params = new URLSearchParams({
        saleId,
        offerId,
        date: startDate,
        nights: nights.toString(),
        guests: travellersSelectionParam,
        sessionId: uuidv4(),
        previousPrice: totalPrice.toString(),
      });

      const checkoutUrl = `${
        /localhost:\d{4}\/flashsales\//.test(window.location.toString()) ? '/flashsales' : ''
      }/sale/checkout?${params.toString()}`;

      window.location.assign(checkoutUrl);
    } else {
      // Old booking form still takes ids without prepending "A"
      const urlOfferId = offerId.substring(1);
      const urlSaleId = saleId.substring(1);
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      const adultsPerRoom = maxAdultsPerRoom || (2 * numberOfRooms) / numberOfRooms;

      const params = new URLSearchParams({
        saleId: urlSaleId,
        offerId: urlOfferId,
        startDate,
        endDate,
        maxAvailableRooms: availableRooms.toString(),
        rooms: numberOfRooms.toString(),
        numberOfAdults: adultsPerRoom.toString(),
        previousPrice: totalPrice.toString(),
      });

      let bookingUrl = `${
        /localhost:\d{4}\/flashsales\//.test(window.location.toString()) ? '/flashsales' : ''
      }/sale/book-hotel?${params.toString()}`;

      for (let i = 0; i < duration; i++) {
        bookingUrl += `&rateCodes=${rateCode}`;
      }

      window.location.assign(bookingUrl);
    }
  }
};
